import PropTypes from "prop-types";
import { Box, Button, Chip, MenuItem, Paper, TextField, Typography } from "@mui/material";
import {
  Refresh,
  LocalParking,
  Storefront,
  WarningAmber,
  Loop,
  History,
} from "@mui/icons-material";
import { parkingDate } from "../../model/parkingModel";
export default function HeaderParking({
  onRefresh,
  loading,
  filtros,
  onFiltroChange,
  onClearFilters,
  tiposComercioUnicos,
  stats,
  total,
  lastUpdated,
}) {
  const filtersActive = Boolean(
    filtros.busqueda ||
      [filtros.tipoComercio, filtros.estado, filtros.estadoCiclos].some(
        (value) => value !== "todos"
      )
  );
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 2 }}>
      <Box
        sx={{
          p: 2.5,
          bgcolor: "#193e54",
          color: "#fff",
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <LocalParking />
        <Box sx={{ flex: "1 1 260px" }}>
          <Typography component="h1" sx={{ fontSize: "1.15rem", fontWeight: 700, color: "#fff" }}>
            Auditoría Parking
          </Typography>
          <Typography sx={{ fontSize: ".8rem", color: "#dbe8f2", mt: 0.5 }}>
            Comercios, terminales y ciclos · Hora de Bogotá
          </Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          disabled={loading}
          onClick={onRefresh}
          sx={{ color: "#fff", "&.Mui-disabled": { color: "#a7bbca" } }}
        >
          {loading ? "Actualizando…" : "Actualizar"}
        </Button>
      </Box>
      <Box sx={{ p: 2.5 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(4,1fr)" },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          {[
            [
              "Comercios filtrados",
              stats.totalComercios,
              "#245a83",
              "#edf4ff",
              "#c9dcf3",
              Storefront,
            ],
            [
              "Con fallo operativo",
              stats.comerciosConFalla,
              "#b42318",
              "#fff0ee",
              "#f3c9c3",
              WarningAmber,
            ],
            [
              "Ciclos abiertos",
              stats.comerciosSinDatosCiclos === stats.totalComercios && stats.totalComercios
                ? "Sin datos"
                : stats.totalCiclosAbiertos,
              "#116979",
              "#e8f7f9",
              "#bce3e8",
              Loop,
            ],
            [
              "Más de 3 días sin ciclos",
              stats.comerciosMas3DiasSinCiclos,
              "#885515",
              "#fff5df",
              "#edd5a6",
              History,
            ],
          ].map(([label, value, color, background, border, MetricIcon]) => (
            <Box
              key={label}
              sx={{
                p: 2,
                border: `1px solid ${border}`,
                borderTop: `3px solid ${color}`,
                borderRadius: 2,
                bgcolor: background,
                position: "relative",
              }}
            >
              <Typography sx={{ color, fontSize: ".75rem", fontWeight: 600, pr: 3 }}>
                {label}
              </Typography>
              <MetricIcon
                sx={{
                  position: "absolute",
                  right: 14,
                  bottom: 17,
                  fontSize: 27,
                  color,
                  opacity: 0.6,
                }}
                aria-hidden="true"
              />
              <Typography
                sx={{
                  color,
                  fontWeight: 700,
                  fontSize: typeof value === "number" ? "1.8rem" : "1rem",
                  mt: 0.5,
                }}
              >
                {typeof value === "number" ? value.toLocaleString("es-CO") : value}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            size="small"
            label="Buscar comercio o terminal"
            value={filtros.busqueda}
            onChange={(e) => onFiltroChange("busqueda", e.target.value)}
            sx={{ flex: "2 1 250px" }}
          />
          {[
            [
              "tipoComercio",
              "Tipo de comercio",
              [["todos", "Todos"], ...tiposComercioUnicos.map((t) => [t, t])],
            ],
            [
              "estado",
              "Estado operativo",
              [
                ["todos", "Todos"],
                ["operativo", "Operativo"],
                ["falla_parcial", "Fallo parcial"],
                ["falla_total", "Fallo total"],
                ["sin_datos", "Sin terminales / Sin datos"],
              ],
            ],
            [
              "estadoCiclos",
              "Ciclos",
              [
                ["todos", "Todos"],
                ["con_ciclos_abiertos", "Con ciclos abiertos"],
                ["sin_ciclos_abiertos", "Sin ciclos abiertos"],
                ["mas_3_dias", "Más de 3 días sin ciclos"],
              ],
            ],
          ].map(([key, label, options]) => (
            <TextField
              key={key}
              select
              size="small"
              label={label}
              value={filtros[key]}
              onChange={(e) => onFiltroChange(key, e.target.value)}
              sx={{ flex: "1 1 170px", "& .MuiInputBase-root": { minHeight: 37 } }}
            >
              {options.map(([value, text]) => (
                <MenuItem key={value} value={value}>
                  {text}
                </MenuItem>
              ))}
            </TextField>
          ))}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
          <Typography sx={{ fontSize: ".75rem", color: "#526579", flex: 1 }}>
            {stats.totalComercios} de {total} comercios · Totales de la vista y filtros
            {lastUpdated ? ` · Actualizado: ${parkingDate(lastUpdated)}` : " · Sin actualización"}
          </Typography>
          <Button size="small" disabled={!filtersActive} onClick={onClearFilters}>
            Limpiar filtros
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
          <Chip
            size="small"
            label={`${stats.totalTerminals} terminales`}
            sx={{ bgcolor: "#e8eff7", color: "#245a83", fontWeight: 600 }}
          />
          <Chip
            size="small"
            label={`${stats.terminalsSinNovedad} terminales sin novedad`}
            sx={{ bgcolor: "#e4f3e9", color: "#217346", fontWeight: 600 }}
          />
          <Chip
            size="small"
            label={`${stats.terminalsPosibleFallo} con posible fallo`}
            sx={{ bgcolor: "#fff1d5", color: "#885515", fontWeight: 600 }}
          />
          <Chip
            size="small"
            label={`${stats.terminalsConNovedad} con novedad`}
            sx={{ bgcolor: "#fde5e2", color: "#b42318", fontWeight: 600 }}
          />
          {stats.terminalsSinDatos > 0 && (
            <Chip size="small" label={`${stats.terminalsSinDatos} terminales sin datos`} />
          )}
          {stats.comerciosSinDatosCiclos > 0 && (
            <Chip
              size="small"
              label={`${stats.comerciosSinDatosCiclos} comercios sin dato de ciclos`}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
}
HeaderParking.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  filtros: PropTypes.object.isRequired,
  onFiltroChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  tiposComercioUnicos: PropTypes.array.isRequired,
  stats: PropTypes.object.isRequired,
  total: PropTypes.number.isRequired,
  lastUpdated: PropTypes.string,
};
